import { useCallback, useRef, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  UpdateWeatherForecastPointItemSchema,
  UpdateWeatherForecastPointsRequestSchema,
  type UpdateWeatherForecastPointsResponse,
} from '@ebalance/proto/bffuser/v1/weather_forecast_point_service_pb'
import { useTranslation } from 'react-i18next'

import { useErrorToast } from '~/hooks/useErrorToast'
import { useListWeatherForecastPoints } from '~/pages/AdminWeatherForecastPoints/hooks/useListWeatherForecastPoints'
import { useUpdateWeatherForecastPoints } from '~/pages/AdminWeatherForecastPoints/hooks/useUpdateWeatherForecastPoints'
import type { WeatherForecastPointRow } from '~/pages/AdminWeatherForecastPoints/models/types'

type UseAreaPointSaveResult = {
  /** 保存中・保存待ちのエリアの選択値 (areaCode → 地点名)。 保存が終われば消える */
  pendingMap: ReadonlyMap<AreaCode, string>
  /** 1 エリアだけを保存する (AC-WFP-003/004) */
  saveArea: (row: WeatherForecastPointRow, locationName: string) => void
}

/**
 * 地点を選んだ時のエリアごとの保存 (AC-WFP-003〜006)。
 *
 * - そのエリアの保存が進行中なら、 最後に選ばれた値だけを保存待ちにして、 進行中の保存が
 *   終わってから送る。 2 回目以降の楽観ロック基準は直前の保存の応答の updatedAt
 *   (自分の保存を他ユーザーの変更として扱わない)
 * - 失敗したエリアだけ pending を捨てて保存済みの地点 (無ければ代表地点) の表示へ戻し、
 *   保存待ちの値も送らない。 競合 (Aborted) は最新値の再取得が終わるまで復旧中として
 *   保存中と同じ扱いにし、 その間に選ばれた値は再取得後の updatedAt で送る
 */
export function useAreaPointSave(): UseAreaPointSaveResult {
  const { t } = useTranslation()
  const showErrorToast = useErrorToast()
  const { refetch } = useListWeatherForecastPoints()
  const updateMutation = useUpdateWeatherForecastPoints()

  const [pendingMap, setPendingMap] = useState<Map<AreaCode, string>>(new Map())
  /** key あり = そのエリアを保存中。 value = 保存待ちの値 (無ければ undefined) */
  const savingRef = useRef(new Map<AreaCode, string | undefined>())

  const clearPending = useCallback((areaCode: AreaCode) => {
    setPendingMap((prev) => {
      if (!prev.has(areaCode)) return prev
      const next = new Map(prev)
      next.delete(areaCode)
      return next
    })
  }, [])

  const run = useCallback(
    async (row: WeatherForecastPointRow, locationName: string) => {
      const { areaCode } = row
      let current = locationName
      let expectedUpdatedAt = row.updatedAt
      try {
        for (;;) {
          let response: UpdateWeatherForecastPointsResponse
          try {
            response = await updateMutation.mutateAsync(
              create(UpdateWeatherForecastPointsRequestSchema, {
                items: [
                  create(UpdateWeatherForecastPointItemSchema, {
                    areaCode,
                    locationName: current,
                    expectedUpdatedAt,
                  }),
                ],
              }),
            )
          } catch (err) {
            if (!(err instanceof ConnectError) || err.code !== Code.Aborted) {
              throw err
            }
            // AC-WFP-005: 楽観ロック競合 → 最新値を再取得して表示し、 エラートーストで再操作を促す。
            // 再取得が終わるまでこのエリアは「復旧中」として保存中と同じ管理 (savingRef) に残す。
            // 先に解くと、 トーストに従って選び直した保存が古い updatedAt で送られ再び競合する。
            // 競合前に積まれていた保存待ちは捨て、 復旧中に選ばれた値だけを再取得後の
            // updatedAt で送る
            showErrorToast({
              description: t('page:adminWeatherForecastPoints.conflict.toast'),
            })
            savingRef.current.set(areaCode, undefined)
            clearPending(areaCode)
            const latest = await refetch()
            const queued = savingRef.current.get(areaCode)
            if (latest.isError || latest.data === undefined) {
              // 復旧中に選び直した値は送れないので、 黙って捨てずに失敗を知らせる
              if (queued !== undefined) {
                showErrorToast({
                  description: t(
                    'page:adminWeatherForecastPoints.error.serverError',
                  ),
                })
              }
              return
            }
            if (queued === undefined) return
            savingRef.current.set(areaCode, undefined)
            current = queued
            expectedUpdatedAt = latest.data.points.find(
              (point) => point.areaCode === areaCode,
            )?.updatedAt
            continue
          }
          const queued = savingRef.current.get(areaCode)
          if (queued === undefined) return
          savingRef.current.set(areaCode, undefined)
          current = queued
          // 応答は全エリアを返す契約。 基準時刻が取れない時に基準なしで送ると
          // 楽観ロックが効かないので、 失敗として扱う
          const updatedAt = response.points.find(
            (point) => point.areaCode === areaCode,
          )?.updatedAt
          if (updatedAt === undefined) {
            throw new Error('update response has no updatedAt for the area')
          }
          expectedUpdatedAt = updatedAt
        }
      } catch {
        // AC-WFP-006: backend の message は内部識別子を含みうるので i18n 文言に寄せる
        showErrorToast({
          description: t('page:adminWeatherForecastPoints.error.serverError'),
        })
      } finally {
        savingRef.current.delete(areaCode)
        clearPending(areaCode)
      }
    },
    [clearPending, refetch, showErrorToast, t, updateMutation],
  )

  const saveArea = useCallback(
    (row: WeatherForecastPointRow, locationName: string) => {
      setPendingMap((prev) => new Map(prev).set(row.areaCode, locationName))
      if (savingRef.current.has(row.areaCode)) {
        savingRef.current.set(row.areaCode, locationName)
        return
      }
      savingRef.current.set(row.areaCode, undefined)
      void run(row, locationName)
    },
    [run],
  )

  return { pendingMap, saveArea }
}
